import React, { createContext, useContext, type ReactNode, useMemo } from 'react';
import { FontFamily, FontWeight } from './Typography.types';
import { getFontClass } from './typography.utils';

/**
 * Typography Context for configuring global font settings
 *
 * Usage:
 *
 * // Global font configuration using Inter Variable Font:
 * <TypographyProvider defaultFontFamily={FontFamily.INTER}>
 *   <App />
 * </TypographyProvider>
 *
 * // Individual component usage with Inter Variable Font:
 * <Heading fontFamily={FontFamily.INTER} fontWeight={FontWeight.SEMIBOLD}>
 *   Hello World
 * </Heading>
 *
 * <Text fontFamily={FontFamily.INTER} variant={TextVariant.H9}>
 *   This is Inter Variable Font text - supports all weights!
 * </Text>
 *
 * Note: FontFamily.INTER now uses a variable font (InterVariable.woff2)
 * which supports the full weight range via CSS font-weight properties.
 */

export interface TypographyContextType {
  defaultFontFamily?: FontFamily;
}

const TypographyContext = createContext<TypographyContextType>({});

export interface TypographyProviderProps {
  children: ReactNode;
  defaultFontFamily?: FontFamily;
}

export const TypographyProvider: React.FC<TypographyProviderProps> = ({
  children,
  defaultFontFamily,
}) => {
  return (
    <TypographyContext.Provider value={{ defaultFontFamily }}>
      {children}
    </TypographyContext.Provider>
  );
};

export const useTypography = (): TypographyContextType => {
  return useContext(TypographyContext);
};

export const useFontClass = (fontWeight?: FontWeight, fontFamily?: FontFamily) => {
  const { defaultFontFamily } = useTypography();
  const effectiveFontFamily = fontFamily || defaultFontFamily;

  const fontClass = useMemo(
    () => getFontClass(fontWeight, effectiveFontFamily),
    [fontWeight, effectiveFontFamily]
  );
  const placeholderFontClass = fontClass.replace('font-', 'placeholder:font-');

  return { fontClass, placeholderFontClass };
};
